import { jsx as _jsx } from "react/jsx-runtime";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/cn";
/**
 * The brand's type scale as components.
 *
 * Two rules from the source page are encoded here and are the ones most worth
 * keeping. First, headings are BOLD and tight: 700 weight with `-0.035em`
 * tracking on h1 and a line height below 1. The public marketing site sets its
 * headings at 400; this is the product voice, and the difference is recorded
 * in docs/research/bpg-brand-data-intelligence.md §8.
 *
 * Second, measures are capped in `ch` on the element itself, not left to a
 * container: 19ch on h1, 24ch on h2, 66ch on a lead, 70ch on a paragraph.
 * That is why the page reads well at any width without a single wrapper.
 *
 * Every one of these takes an `as` prop, because the heading LEVEL is a
 * document-structure decision and the SIZE is a visual one. A section's first
 * heading might be an `h2` that has to look like an `h1`.
 */
const headingBase = "lg:font-bold lg:text-strong lg:text-balance";
export const h1Variants = cva([headingBase, "lg:text-h1 lg:max-w-[19ch]"], {
    variants: { wide: { true: "lg:max-w-[24ch]", false: "" } },
    defaultVariants: { wide: false },
});
export function H1({ className, wide, as, ...props }) {
    const Component = as ?? "h1";
    return _jsx(Component, { "data-slot": "h1", className: cn(h1Variants({ wide }), className), ...props });
}
export function H2({ className, as, ...props }) {
    const Component = as ?? "h2";
    return (_jsx(Component, { "data-slot": "h2", className: cn(headingBase, "lg:text-h2 lg:max-w-[24ch]", className), ...props }));
}
export function H3({ className, as, ...props }) {
    const Component = as ?? "h3";
    return (_jsx(Component, { "data-slot": "h3", className: cn(headingBase, "lg:text-h3", className), ...props }));
}
export const textVariants = cva("", {
    variants: {
        size: {
            lead: "lg:text-lead lg:text-muted-foreground lg:max-w-lead",
            body: "lg:text-body lg:max-w-measure",
            sm: "lg:text-body-sm lg:max-w-measure",
            caption: "lg:text-caption",
        },
        tone: {
            default: "lg:text-foreground",
            muted: "lg:text-muted-foreground",
            /** The 3.66:1 tier. Decorative and tertiary only, never load bearing. */
            subtle: "lg:text-subtle",
            strong: "lg:text-strong",
            primary: "lg:text-primary",
            danger: "lg:text-destructive",
            success: "lg:text-success",
        },
    },
    defaultVariants: { size: "body", tone: "default" },
});
export function Text({ className, size, tone, as, ...props }) {
    const Component = as ?? "p";
    return (_jsx(Component, { "data-slot": "text", className: cn(textVariants({ size, tone }), className), ...props }));
}
export const eyebrowVariants = cva([
    "lg:inline-block lg:font-label lg:text-eyebrow lg:font-medium",
    "lg:uppercase lg:tracking-eyebrow",
], {
    variants: {
        tone: {
            primary: "lg:text-primary",
            neutral: "lg:text-muted-foreground",
            subtle: "lg:text-subtle",
        },
    },
    defaultVariants: { tone: "primary" },
});
/**
 * The label above a heading. It is decoration in the document sense: it is not
 * a heading, so it does not get a heading tag, and screen reader users reach
 * the real heading immediately after it.
 */
export function Eyebrow({ className, tone, as, ...props }) {
    const Component = as ?? "span";
    return (_jsx(Component, { "data-slot": "eyebrow", className: cn(eyebrowVariants({ tone }), className), ...props }));
}
/**
 * Mono is the brand's label voice, not a code font. Anything that names,
 * labels, counts or identifies is set in it: identifiers, timestamps,
 * quantities, statuses. Prose is not.
 */
export function Mono({ className, as, tabular = true, ...props }) {
    const Component = as ?? "span";
    return (_jsx(Component, { "data-slot": "mono", className: cn("lg:font-mono", tabular && "lg:tabular-nums", className), ...props }));
}
//# sourceMappingURL=typography.js.map